import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useAudioNoteRecorder } from '@/composables/useAudioNoteRecorder'
import { useToast } from '@/components/ui/toast/use-toast'
import { useLogger } from '@/composables/useLogger'
import type { AudioNoteResponse } from '@/types/audio'

export const useAudioNoteStore = defineStore('audioNote', () => {
  const { toast } = useToast()
  const { log, logError } = useLogger()

  // Get the composable instance
  const audioRecorder = useAudioNoteRecorder()

  // Store-specific state
  const isAnalyzing = ref(false)
  const currentBubbleUuid = ref<string | null>(null)
  const analyzedData = ref<AudioNoteResponse | null>(null)

  const setBubbleUuid = (uuid: string) => {
    currentBubbleUuid.value = uuid
    log('Setting bubble UUID for audio note:', uuid)
  }

  const startRecording = async (onResponse: (response: AudioNoteResponse) => void) => {
    try {
      log('Starting audio recording')
      isAnalyzing.value = true
      await audioRecorder.startRecording(async (response) => {
        log('Received audio analysis response:', response)
        analyzedData.value = response
        onResponse(response)
      })
    } catch (error) {
      logError('Error in recording:', error)
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to record audio',
        variant: 'destructive',
      })
    } finally {
      isAnalyzing.value = false
      log('Finished audio recording process')
    }
  }

  const stopRecording = () => {
    log('Stopping audio recording')
    audioRecorder.stopRecording()
  }

  return {
    // State
    isRecording: audioRecorder.isRecording,
    recordingTime: audioRecorder.recordingTime,
    audioLevel: audioRecorder.audioLevel,
    isAnalyzing,
    currentBubbleUuid,
    analyzedData,

    // Actions
    setBubbleUuid,
    startRecording,
    stopRecording,
  }
})
